import React from 'react';
import { useForm } from 'react-hook-form';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PasswordField from '../../../Base/Components/Inputs/PasswordField/PasswordField';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import apiClient from '../../../../Services/ApiClient';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useNotification from '../../../../Hooks/useNotification.hook';
import useLoading from '../../../Base/Hooks/useLoading';
import Text from '../../../../Components/Base/Text/Text';
import styles from './UserSettingsPassword.module.scss';

interface Props {
	userId: string;
	onSuccess: () => void;
}

interface UserSettingsPasswordValues {
	oldPassword: string;
	password: string;
	passwordConfirmation: string;
}

const validationSchema = yup.object({
	oldPassword: yup
		.string()
		.required('component.newPasswordSetup.passwordRequired'),
	password: yup
		.string()
		.required('component.newPasswordSetup.passwordRequired'),
	passwordConfirmation: yup
		.string()
		.required('component.newPasswordSetup.passwordRequired')
		.oneOf(
			[yup.ref('password'), null],
			'component.newPasswordSetup.passwordMachValidationMessage',
		),
});

const UserSettingsPassword: React.FC<Props> = ({ userId, onSuccess }) => {
	const { isLoading, setIsLoading, RenderLoading } = useLoading();
	const { displayError } = useApiClient();
	const { showNotificationSuccess } = useNotification();

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
	});

	const validatePassword = async (password: string): Promise<boolean> => {
		try {
			const response = await apiClient.post('/user/password-validation', {
				password,
			});
			return response.status === 200;
		} catch (e) {
			displayError(e);
		}
		return false;
	};

	const updatePassword = async (values: UserSettingsPasswordValues) => {
		try {
			const response = await apiClient.patch(`/user/${userId}`, {
				oldPassword: values.oldPassword,
				password: values.password,
			});
			if (response.status === 200) {
				showNotificationSuccess({
					content: 'component.passwordChange.pswChanged',
				});
				onSuccess();
			}
		} catch (e) {
			displayError(e);
		}
	};

	const handleSubmit = async (values: UserSettingsPasswordValues) => {
		setIsLoading(true);
		const isValid = await validatePassword(values.password);
		if (isValid) {
			await updatePassword(values);
		}
		setIsLoading(false);
	};

	if (isLoading) {
		return <RenderLoading />;
	}

	return (
		<FormWrapper formMethods={formMethods}>
			<Text
				tx='component.passwordChange.hintText'
				className={styles.hintText}
			/>
			<PasswordField
				name='oldPassword'
				label='component.passwordChange.enterYourPsw'
				isRequired
				isWrapped
			/>
			<PasswordField
				name='password'
				label='component.passwordChange.newPsw'
				loadChecklist
				isRequired
				isWrapped
			/>
			<PasswordField
				name='passwordConfirmation'
				label='component.passwordChange.reEnterNewPsw'
				isRequired
				isWrapped
			/>
			<div className='mt-3'>
				<SubmitButton onSubmit={handleSubmit} title='base.shared.save' />
			</div>
		</FormWrapper>
	);
};

export default UserSettingsPassword;
